import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { PiReadCvLogo } from "react-icons/pi";
import { Edit, ArrowForwardIosSharp } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";

import Layout from "../templates/Layout";
import styles from "../Button/Button.module.css";
import { pathToMapCleaningLog } from "../../helpers/paths";
import Button from "../Button/Button";
import LogTable from "./CleaningLog/LogTable";
import { MapViewer } from "../Form/MapViewer";
import { SettingMapDialog } from "./Setting/SettingMap/SettingMapDialog";
import { useListRobotQuery } from "hooks/worker/useRobot";
import { useListMapQuery } from "hooks/worker/useMap";
import { useListCleaningEventByMapQuery } from "hooks/worker/useCleaningEvent";
import { formatTime } from "utils/time";
import { ScheduleItem } from "types/response/schedule";
import { useSafeQueryId } from "hooks/useSafeId";
import DeleteConfirmModal from "component/Modal/DeleteConfirmModal";
import { useDeleteMapModal } from "hooks/useDeleteMapModal";

const weekTexts = ["日", "月", "火", "水", "木", "金", "土"];

type ScheduleWithRobotName = ScheduleItem & {
  robotName: string;
};

const MapDetail = () => {
  const navigate = useNavigate();
  const mapId = useSafeQueryId("mapId");
  const deleteModal = useDeleteMapModal();

  const [open, setOpen] = useState(false);
  const [schedules, setSchedules] = useState<ScheduleWithRobotName[]>([]);
  const { data: robots = [] } = useListRobotQuery();
  const { data: maps = [] } = useListMapQuery();
  const map = maps.find((m) => m.mapId === mapId);
  const { data: logs = [] } = useListCleaningEventByMapQuery({ mapId });

  useEffect(() => {
    setSchedules(
      robots
        .flatMap((r) =>
          r.schedules
            .filter((s) => s.mapId === mapId)
            .map((s) => ({ ...s, robotName: r.robotName }))
        )
        .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
    );
  }, [robots, mapId]);

  // 清掃ログボタン押下時処理
  const onClickMapCleaningLog = () => {
    if (!mapId) {
      return;
    }
    navigate(pathToMapCleaningLog(mapId));
  };

  // マップ名リンク押下時処理
  const onClickEditInfo = useCallback(() => {
    // Modalウインドウ表示フラグ切り替え
    setOpen(true);
  }, []);
  // Modalウインドウ外をクリックしたときにModalウインドウを閉じる
  const handleClose = () => {
    // 編集した後に保存せずウインドウを閉じた場合に修正内容をリセットする
    setOpen(false);
  };

  // 保存ボタン押下時処理
  const onClickSave = async () => {
    // await queryClient.invalidateQueries([buildingId, "maps"]);
    setOpen(false);
  };

  return (
    <Layout title={`マップ詳細画面`}>
      <Card sx={{ minWidth: 250, overflowWrap: "anywhere" }}>
        <Box className={styles.buttonWrapper2}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <CardHeader
              title={map?.mapName}
              titleTypographyProps={{ variant: "h5" }}
              subheader={
                map?.description && (
                  <Accordion sx={{ border: "none", boxShadow: "none" }}>
                    <AccordionSummary
                      expandIcon={
                        <ArrowForwardIosSharp
                          sx={{ fontSize: (theme) => theme.typography.h6 }}
                        />
                      }
                      sx={{
                        flexDirection: "row-reverse",
                        "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded":
                          {
                            transform: "rotate(90deg)",
                          },
                        "& .MuiAccordionSummary-content": {
                          ml: 1,
                        },
                      }}
                    >
                      説明
                    </AccordionSummary>
                    <AccordionDetails sx={{ whiteSpace: "pre-wrap" }}>
                      {map?.description}
                    </AccordionDetails>
                  </Accordion>
                )
              }
            />
            <Box display={"flex"}>
              <IconButton
                aria-label="edit a map"
                color="error"
                onClick={() => {
                  deleteModal.onOpen(mapId);
                }}
                sx={{ m: "8px 16px auto auto" }}
              >
                <DeleteIcon />
              </IconButton>
              <IconButton
                aria-label="edit a map"
                color="primary"
                onClick={onClickEditInfo}
                sx={{ m: "8px 16px auto auto" }}
              >
                <Edit />
              </IconButton>
            </Box>
          </Box>
        </Box>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 2,
            }}
          >
            {map && logs.length > 0 && logs[0].imagePath && (
              <MapViewer
                src={logs[0].imagePath}
                displaySetting={map.displaySetting}
              />
            )}
          </Box>
          <Box sx={{ display: "flex", mt: 4, mb: 1 }}>
            <Typography variant="h6">清掃スケジュール</Typography>
          </Box>

          <TableContainer component={Card}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>曜日</TableCell>
                  <TableCell>開始時刻</TableCell>
                  <TableCell>ロボット名</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {schedules.map((s) => {
                  return (
                    <TableRow key={s.scheduleId}>
                      <TableCell>{weekTexts[s.dayOfWeek]}</TableCell>
                      <TableCell>
                        {s.robotName ? `${formatTime(s.hour, s.minute)}` : ""}
                      </TableCell>
                      <TableCell>{s.robotName || "清掃無し"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", mt: 4, mb: 1, alignItems: "center" }}>
            <Typography variant="h6">直近の清掃ログ</Typography>
            <Button
              sx={{
                ml: "auto",
                justifyContent: "center",
              }}
              variant="outlined"
              onClick={onClickMapCleaningLog}
              startIcon={<PiReadCvLogo />}
            >
              清掃ログ
            </Button>
          </Box>
          <Box>
            <Box>
              {map && (
                <LogTable
                  logs={logs}
                  robots={robots}
                  maps={[map]}
                  enableRobotName
                  displayLimit={5}
                />
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
      {map && open && (
        <SettingMapDialog
          open={open}
          map={map}
          latestLog={logs[0]}
          onClickSave={onClickSave}
          onClickCancel={handleClose}
          hideEnabledSetting
        />
      )}
      {map && (
        <DeleteConfirmModal
          open={deleteModal.open}
          onClose={deleteModal.onClose}
          data={map}
          label="マップ"
          format={(m) => m.mapName}
          isConfirming={deleteModal.isLoading}
          onConfirm={deleteModal.onDelete}
        />
      )}
    </Layout>
  );
};

export default MapDetail;
